/**
 * WhatsApp Business API client.
 * Base URL: REACT_APP_API_URL (or REACT_APP_ERP_BASE_URL) via getErpBaseUrl() → /api/whatsapp
 */
import axios from "axios";
import {
  erpFetch,
  getErpRequestErrorMessage,
  getErpBaseUrl,
  getErpToken,
} from "../erpClient";

const WHATSAPP_PREFIX = "/whatsapp";

export { getErpRequestErrorMessage as getWhatsAppErrorMessage };

export function isWhatsAppForbiddenError(err) {
  return err?.response?.status === 403;
}

export function isWhatsAppApiSuccess(res) {
  return (
    res &&
    (res.success === true ||
      String(res.status || "").toLowerCase() === "success" ||
      res.message === "radha")
  );
}

export function unwrapWhatsAppList(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
}

async function whatsappMultipart(path, formData, { onUploadProgress } = {}) {
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}${WHATSAPP_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await axios.post(url, formData, {
    headers,
    onUploadProgress: onUploadProgress
      ? (event) => {
          if (event.total) {
            onUploadProgress(Math.round((event.loaded * 100) / event.total));
          } else {
            onUploadProgress(null);
          }
        }
      : undefined,
  });
  return response.data;
}

export function isWhatsAppSessionExpiredResponse(res) {
  return res?.sessionExpired === true;
}

export async function uploadWhatsAppMedia(
  file,
  { convert = false, onUploadProgress } = {},
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("convert", convert ? "YES" : "NO");
  return whatsappMultipart("/media/upload", formData, { onUploadProgress });
}

export function sendWhatsAppImage(chatId, { url, caption } = {}) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/send-image`,
    {
      method: "POST",
      body: {
        url,
        ...(caption ? { caption } : {}),
      },
      skipFinancialYear: true,
    },
  );
}

export function sendWhatsAppVideo(chatId, { url, caption } = {}) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/send-video`,
    {
      method: "POST",
      body: {
        url,
        ...(caption ? { caption } : {}),
      },
      skipFinancialYear: true,
    },
  );
}

export function sendWhatsAppAudio(chatId, { url } = {}) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/send-audio`,
    {
      method: "POST",
      body: { url },
      skipFinancialYear: true,
    },
  );
}

export function sendWhatsAppDocument(chatId, { url, caption, filename } = {}) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/send-document`,
    {
      method: "POST",
      body: {
        url,
        ...(caption ? { caption } : {}),
        ...(filename ? { filename } : {}),
      },
      skipFinancialYear: true,
    },
  );
}

export async function sendWhatsAppMediaMessage(
  chatId,
  type,
  { url, caption, filename } = {},
) {
  if (type === "image") {
    return sendWhatsAppImage(chatId, { url, caption });
  }
  if (type === "video") {
    return sendWhatsAppVideo(chatId, { url, caption });
  }
  if (type === "audio") {
    return sendWhatsAppAudio(chatId, { url });
  }
  if (type === "document") {
    return sendWhatsAppDocument(chatId, { url, caption, filename });
  }
  throw new Error(`Unsupported media type: ${type}`);
}

export function listChats({ search = "", limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const qs = params.toString();
  return erpFetch(`${WHATSAPP_PREFIX}/chats?${qs}`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

export function listMessages(chatId, { limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const qs = params.toString();
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/messages?${qs}`,
    {
      method: "GET",
      dedupeInFlight: true,
      skipFinancialYear: true,
    },
  );
}

export function sendTextMessage(chatId, text) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/send-text`,
    {
      method: "POST",
      body: { text },
      skipFinancialYear: true,
    },
  );
}

export function markChatRead(chatId) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/read`,
    {
      method: "POST",
      body: {},
      skipFinancialYear: true,
    },
  );
}

export function sendTemplateMessage(chatId, payload) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/send-template`,
    {
      method: "POST",
      body: payload,
      skipFinancialYear: true,
    },
  );
}

// ---------- Chat detail (inbox right sidebar) ----------

export function getChatDetail(chatId) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}`,
    {
      method: "GET",
      dedupeInFlight: true,
      skipFinancialYear: true,
    },
  );
}

export function getChatContact(chatId) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/contact`,
    {
      method: "GET",
      dedupeInFlight: true,
      skipFinancialYear: true,
    },
  );
}

export function saveChatContact(chatId, payload) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/save-contact`,
    {
      method: "POST",
      body: payload,
      skipFinancialYear: true,
    },
  );
}

export function createChatNote(chatId, payload) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/notes`,
    {
      method: "POST",
      body: payload,
      skipFinancialYear: true,
    },
  );
}

export function updateChatNote(chatId, noteId, payload) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: "PUT",
      body: payload,
      skipFinancialYear: true,
    },
  );
}

export function deleteChatNote(chatId, noteId) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: "DELETE",
      skipFinancialYear: true,
    },
  );
}

export function clearChatNotes(chatId) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/notes`,
    {
      method: "DELETE",
      skipFinancialYear: true,
    },
  );
}

export function listChatMedia(
  chatId,
  { type = "all", limit = 20, offset = 0 } = {},
) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const qs = params.toString();
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/media?${qs}`,
    {
      method: "GET",
      dedupeInFlight: true,
      skipFinancialYear: true,
    },
  );
}

export function getChatMediaCounts(chatId) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/chats/${encodeURIComponent(chatId)}/media/counts`,
    {
      method: "GET",
      dedupeInFlight: true,
      skipFinancialYear: true,
    },
  );
}

// ---------- Phonebooks ----------

export function listPhonebooks() {
  return erpFetch(`${WHATSAPP_PREFIX}/phonebooks`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

export function createPhonebook(name) {
  return erpFetch(`${WHATSAPP_PREFIX}/phonebooks`, {
    method: "POST",
    body: { name },
    skipFinancialYear: true,
  });
}

export function deletePhonebook(id) {
  return erpFetch(`${WHATSAPP_PREFIX}/phonebooks/${encodeURIComponent(id)}`, {
    method: "DELETE",
    skipFinancialYear: true,
  });
}

export function listPhonebookContacts(
  phonebookId,
  { search = "", limit = 50, offset = 0 } = {},
) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const qs = params.toString();
  return erpFetch(
    `${WHATSAPP_PREFIX}/phonebooks/${encodeURIComponent(phonebookId)}/contacts?${qs}`,
    {
      method: "GET",
      dedupeInFlight: true,
      skipFinancialYear: true,
    },
  );
}

export function addPhonebookContact(phonebookId, payload) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/phonebooks/${encodeURIComponent(phonebookId)}/contacts`,
    {
      method: "POST",
      body: payload,
      skipFinancialYear: true,
    },
  );
}

export function updateContact(contactId, payload) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/contacts/${encodeURIComponent(contactId)}`,
    {
      method: "PUT",
      body: payload,
      skipFinancialYear: true,
    },
  );
}

export function deleteContacts(ids) {
  return erpFetch(`${WHATSAPP_PREFIX}/contacts`, {
    method: "DELETE",
    body: { ids },
    skipFinancialYear: true,
  });
}

export function importPhonebookContactsCsv(phonebookId, file) {
  const formData = new FormData();
  formData.append("file", file);
  return whatsappMultipart(
    `/phonebooks/${encodeURIComponent(phonebookId)}/contacts/import`,
    formData,
  );
}

// ---------- Templates ----------

export function listTemplates({
  status = "APPROVED",
  limit = 10,
  page = 1,
  search = "",
} = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(Math.min(Number(limit) || 10, 10)));
  params.set("page", String(page));
  if (search) params.set("search", search);
  const qs = params.toString();
  return erpFetch(`${WHATSAPP_PREFIX}/templates?${qs}`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

export function createTemplate(payload) {
  return erpFetch(`${WHATSAPP_PREFIX}/templates`, {
    method: "POST",
    body: payload,
    skipFinancialYear: true,
  });
}

export function uploadTemplateMedia(file) {
  const formData = new FormData();
  formData.append("file", file);
  return whatsappMultipart("/templates/media", formData);
}

export function deleteTemplate(name) {
  return erpFetch(
    `${WHATSAPP_PREFIX}/templates/${encodeURIComponent(name)}`,
    {
      method: "DELETE",
      skipFinancialYear: true,
    },
  );
}

// ---------- Campaigns ----------

export function listCampaigns({
  page = 1,
  limit = 10,
  status = "",
  search = "",
} = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(Math.min(Number(limit) || 10, 10)));
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  const qs = params.toString();
  return erpFetch(`${WHATSAPP_PREFIX}/campaigns?${qs}`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

export function createCampaign(payload) {
  return erpFetch(`${WHATSAPP_PREFIX}/campaigns`, {
    method: "POST",
    body: payload,
    skipFinancialYear: true,
  });
}

export function getCampaign(id) {
  return erpFetch(`${WHATSAPP_PREFIX}/campaigns/${encodeURIComponent(id)}`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

export function deleteCampaign(id) {
  return erpFetch(`${WHATSAPP_PREFIX}/campaigns/${encodeURIComponent(id)}`, {
    method: "DELETE",
    skipFinancialYear: true,
  });
}

export async function exportCampaignCsv(campaignId) {
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}${WHATSAPP_PREFIX}/campaigns/${encodeURIComponent(campaignId)}/export`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await axios.get(url, { headers, responseType: "blob" });
  return response.data;
}

// ---------- Dashboard ----------

export function getWhatsAppDashboard() {
  return erpFetch(`${WHATSAPP_PREFIX}/dashboard`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

// ---------- Tenant settings (Meta credentials) ----------

export function getWhatsAppSettings() {
  return erpFetch(`${WHATSAPP_PREFIX}/settings`, {
    method: "GET",
    dedupeInFlight: true,
    skipFinancialYear: true,
  });
}

export function saveWhatsAppSettings(payload) {
  return erpFetch(`${WHATSAPP_PREFIX}/settings`, {
    method: "PUT",
    body: payload,
    skipFinancialYear: true,
  });
}
